// ──────────────────────────────────────────────
// classifyPipelineError — classifies storage/pipeline init errors
// into user-friendly types with actionable messages.
// ──────────────────────────────────────────────

import type { StorageFailureMode } from '@services/storage/classifyStorageError';
import { StorageInitError } from '@services/storage/classifyStorageError';

/** Where in the pipeline the failure occurred. */
export type PipelineErrorSource =
  | 'storage_init'
  | 'storage_not_ready'
  | 'session_completion'
  | 'audio_device'
  | 'recording_start';

/** User-friendly classification derived from the raw error. */
export type PipelineErrorType =
  | 'storage_quota'
  | 'storage_security'
  | 'storage_corrupted'
  | 'storage_unavailable'
  | 'session_init_failed'
  | 'unknown';

export interface PipelineError {
  type: PipelineErrorType;
  source: PipelineErrorSource;
  message: string;
  retryCount: number;
  raw: unknown;
}

/** User-facing copy keyed by error type. */
const USER_MESSAGES: Record<PipelineErrorType, string> = {
  storage_quota:
    "Your browser's storage is full. Please close other tabs or clear site data, then try again.",
  storage_security:
    'Your browser is blocking storage access. Check that cookies and site data are enabled for this site.',
  storage_corrupted:
    "Your browser's storage appears to be corrupted. Clearing site data should fix the problem.",
  storage_unavailable:
    'Browser storage is unavailable. Please try a different browser or check your privacy settings.',
  session_init_failed:
    'The recording session could not be initialized. Please try again.',
  unknown:
    'Something went wrong while preparing the recording pipeline. Please try again.',
};

/**
 * Classifies a raw error + source into a `PipelineError` with a user-friendly message.
 */
export function classifyPipelineError(
  source: PipelineErrorSource,
  error: unknown,
  retryCount: number,
): PipelineError {
  const type = deriveType(source, error);
  const base = USER_MESSAGES[type];
  const message =
    retryCount >= 3
      ? `${base} If this issue persists, please contact support.`
      : base;

  return { type, source, message, retryCount, raw: error };
}

/** Maps a low-level storage failure mode onto the user-facing pipeline error type. */
const FAILURE_MODE_TO_TYPE: Record<StorageFailureMode, PipelineErrorType> = {
  quota_exceeded: 'storage_quota',
  access_denied: 'storage_security',
  corrupted_db: 'storage_corrupted',
  version_conflict: 'storage_corrupted',
  blocked_db: 'storage_corrupted',
  internal_error: 'storage_unavailable',
  unknown: 'unknown',
};

function deriveType(source: PipelineErrorSource, error: unknown): PipelineErrorType {
  if (source === 'session_completion') return 'session_init_failed';

  // Prefer the pre-classified failure mode when the storage layer already
  // wrapped the raw DOMException — this keeps the (browser-name → root-cause)
  // mapping in a single place instead of duplicating it here.
  if (error instanceof StorageInitError) {
    return FAILURE_MODE_TO_TYPE[error.failureMode];
  }

  // DOMException-based classification (IndexedDB / OPFS errors)
  if (error instanceof DOMException) {
    const name = error.name;
    if (name === 'QuotaExceededError') return 'storage_quota';
    if (name === 'SecurityError' || name === 'NotAllowedError') return 'storage_security';
    // VersionError, InvalidStateError, AbortError → likely corruption
    if (
      name === 'VersionError' ||
      name === 'InvalidStateError' ||
      name === 'AbortError'
    ) {
      return 'storage_corrupted';
    }
    // UnknownError → browser-level internal/I/O failure (the SMARTSCRIBE-WEBAPP-V6
    // bucket). Distinct from a truly unclassified error, so it gets its own type.
    if (name === 'UnknownError') return 'storage_unavailable';
  }

  // String-based heuristic for wrapped errors
  const msg = error instanceof Error ? error.message : String(error);
  const lower = msg.toLowerCase();

  if (lower.includes('quota')) return 'storage_quota';
  if (lower.includes('security') || lower.includes('not allowed')) return 'storage_security';
  if (lower.includes('corrupt') || lower.includes('version')) return 'storage_corrupted';
  if (lower.includes('storage') || lower.includes('indexeddb') || lower.includes('opfs')) {
    return 'storage_unavailable';
  }

  return 'unknown';
}
